import type { ZodTypeAny } from 'zod';

/**
 * Get a nested schema from a zod schema
 * @param schema - The zod schema
 * @param path - The path to the nested schema
 * @returns The nested schema
 */
export function getNestedSchema(
  schema: ZodTypeAny | undefined,
  path: string
): ZodTypeAny | undefined {
  // If the schema or path is undefined, return undefined
  if (!schema || !path) return undefined;

  // Split the path and reduce it to the nested schema
  return path.split('.').reduce<ZodTypeAny | undefined>((current, part) => {
    if (!current) return undefined;

    // Try direct object shape
    const directShape =
      'shape' in current ? (current.shape as Record<string, ZodTypeAny>) : undefined;
    if (directShape?.[part]) return directShape[part];

    // Unwrap common wrapper types (optional/nullable/effects/array)
    const def = '_def' in current ? (current._def as Record<string, unknown>) : undefined;
    const candidates: (ZodTypeAny | undefined)[] = [];
    if (def && 'innerType' in def) candidates.push(def.innerType as ZodTypeAny);
    if (def && 'type' in def) candidates.push(def.type as ZodTypeAny);
    if (def && 'schema' in def) candidates.push(def.schema as ZodTypeAny);

    for (const candidate of candidates) {
      if (!candidate) continue;
      const candidateShape =
        'shape' in candidate ? (candidate.shape as Record<string, ZodTypeAny>) : undefined;
      if (candidateShape?.[part]) return candidateShape[part];
    }

    return undefined;
  }, schema);
}

/**
 * Extract the description from a zod type
 * @param zodType - The zod type
 * @returns The description
 */
export function extractDescriptionFromZodType(zodType?: ZodTypeAny): string | undefined {
  // If the zod type is undefined or does not have a _def property, return undefined
  if (!zodType || !('_def' in zodType)) {
    return undefined;
  }

  // Get the definition of the zod type
  const def = zodType._def;

  // If the description is a string, return it
  if (def.description && typeof def.description === 'string') {
    return def.description;
  }

  // If no direct description, try to find it in nested schemas (effects/optional/array)
  const nested: unknown[] = [
    // z.ZodEffects
    (def as unknown as { schema?: ZodTypeAny }).schema,
    // z.ZodOptional / z.ZodNullable
    (def as unknown as { innerType?: ZodTypeAny }).innerType,
    // z.ZodArray
    (def as unknown as { type?: ZodTypeAny }).type
  ];

  for (const next of nested) {
    if (next && typeof next === 'object' && '_def' in (next as ZodTypeAny)) {
      const result = extractDescriptionFromZodType(next as ZodTypeAny);
      if (result) return result;
    }
  }

  // If the description is not a string, return undefined
  return undefined;
}

/**
 * Whether a zod type carries a given wrapper (or terminal) typeName anywhere in its
 * unwrapped chain (optional/nullable/effects/coerce...).
 *
 * Deliberately structural (reads `_def.typeName`) instead of behavioral (`safeParse`):
 * `.isOptional()`/`.isNullable()` run the input through the whole validator, and
 * `z.coerce.number()` coerces `null`/`undefined` via `Number(...)` before validating —
 * `Number(null) === 0`, so a coerced numeric field with `.min(0)` makes `safeParse(null)`
 * succeed even though `.nullable()` was never called. A structural check can't be fooled
 * by that coercion side effect.
 */
function hasZodTypeName(zodType: ZodTypeAny, typeName: string): boolean {
  if (!('_def' in zodType)) return false;

  const def = zodType._def as Record<string, unknown>;
  if (def.typeName === typeName) return true;

  const inner = (def.innerType ?? def.schema) as ZodTypeAny | undefined;
  return inner ? hasZodTypeName(inner, typeName) : false;
}

/**
 * Whether a zod type carries `coerce: true` anywhere in its unwrap chain.
 * Coercion can turn `undefined` into a valid value (`String(undefined)`, `Boolean(undefined)`),
 * which would fool a behavioral `safeParse(undefined)` check into reporting a genuinely
 * required to be coerced field as optional.
 */
function hasCoerce(zodType: ZodTypeAny): boolean {
  if (!('_def' in zodType)) return false;

  const def = zodType._def as Record<string, unknown>;
  if (def.coerce === true) return true;

  const inner = (def.innerType ?? def.schema) as ZodTypeAny | undefined;
  return inner ? hasCoerce(inner) : false;
}

/**
 * Whether a zod type accepts `undefined` without being structurally wrapped in
 * `ZodOptional`. Needed for opaque types like `z.custom(...)`, which compiles to
 * `ZodEffects` around `ZodAny` — its "optionality" only shows up by actually running
 * the check function, not by inspecting `_def`. Skipped for coerced types (see `hasCoerce`).
 */
function acceptsUndefinedBehaviorally(zodType: ZodTypeAny): boolean {
  if (hasCoerce(zodType)) return false;

  try {
    return zodType.safeParse(undefined).success;
  } catch {
    return false;
  }
}

/**
 * Whether a field should show the required-marker (*): not optional, not a boolean
 * (checkbox/switch/radio-as-boolean have no meaningful "unset" state), and doesn't
 * itself accept `undefined` (e.g., a `z.custom` check that allows it).
 * @param zodType - The zod type of the field
 */
export function isFieldRequired(zodType?: ZodTypeAny): boolean {
  if (!zodType) return false;
  if (hasZodTypeName(zodType, 'ZodOptional')) return false;
  if (hasZodTypeName(zodType, 'ZodBoolean')) return false;
  return !acceptsUndefinedBehaviorally(zodType);
}
